import { ChevronRightIcon, HeadsetIcon, IdCardLanyardIcon, LogOutIcon } from '@/components/atoms/icons';
import { BuildingIcon } from '@/components/atoms/icons/BuildingIcon';
import { CircleQuestionMarkIcon } from '@/components/atoms/icons/CircleQuestionMarkIcon';
import { Typography } from '@/components/atoms/Typography';
import { ConfirmationDialog, OkDialog } from '@/components/molecules';
import { useAuth } from '@/core/contexts/AuthContext';
import { useIsAdhaJourney, useUIColors } from '@/core/hooks';
import { useTranslation } from '@/core/hooks/useTranslation';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { LanguageSwitch } from '@/modules/profile/components/LanguageSwitch';
import { router } from 'expo-router';
import { navigateToWelcomeAfterLogout } from '@/core/navigation/logoutNavigation';
import {
    trackProfileLogoutConfirm,
    trackProfileLogoutError,
    trackProfileLogoutPress,
    trackProfileLogoutSuccess,
} from '@/core/services/trackDynatraceCatalog';
import { visualPxToLayoutPx } from '@/core/utils/viewportMetrics';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, StyleSheet, View, useWindowDimensions, Platform } from 'react-native';

export interface UserProfileDropdownProps {
    visible: boolean;
    onClose: () => void;
    anchorPosition?: { x: number; y: number };
}

function ProfileMenuRow({
    isRTL,
    borderColor,
    iconColor,
    label,
    onPress,
    icon,
}: Readonly<{
    isRTL: boolean;
    borderColor: string;
    iconColor: string;
    label: string;
    onPress: () => void;
    icon: React.ReactNode;
}>) {
    return (
        <Pressable
            style={[styles.menuItem, { borderColor }]}
            onPress={onPress}
        >
            {icon}
            <Typography
                variant="bodyLarge"
                color="primary"
                style={[styles.menuItemText, { textAlign: isRTL ? 'right' : 'left' }]}
            >
                {label}
            </Typography>
            <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                <ChevronRightIcon size={24} color={iconColor} />
            </View>
        </Pressable>
    );
}

export function UserProfileDropdown({ visible, onClose, anchorPosition }: Readonly<UserProfileDropdownProps>) {
    const colors = useUIColors();
    const { t, currentLanguage, changeLanguage } = useTranslation();
    const { isRTL } = useI18nContext();
    const { logout } = useAuth();
    const isAdhaJourney = useIsAdhaJourney();

    const [dropdownPosition, setDropdownPosition] = useState<{
        top: number;
        left?: number;
        right?: number;
        width?: number;
    } | null>(null);
    const [logoutDialogVisible, setLogoutDialogVisible] = useState(false);
    const [logoutErrorVisible, setLogoutErrorVisible] = useState(false);
    const prevLanguageRef = useRef<string>(currentLanguage);

    // Close dropdown when language changes
    useEffect(() => {
        if (visible && prevLanguageRef.current !== currentLanguage) {
            onClose();
        }
        prevLanguageRef.current = currentLanguage;
    }, [currentLanguage, visible, onClose]);

    const { width: windowWidth } = useWindowDimensions();
    const dropdownWidth = Math.min(423, windowWidth - 32);

    // Calculate dropdown position
    useEffect(() => {
        if (visible && anchorPosition && typeof window !== 'undefined') {
            // measureInWindow returns visual (post-zoom) coords; Modal absolute
            // styles use pre-zoom layout coords when html { zoom } is active.
            const anchorRight = Platform.OS === 'web'
                ? visualPxToLayoutPx(anchorPosition.x)
                : anchorPosition.x;
            const anchorBottom = Platform.OS === 'web'
                ? visualPxToLayoutPx(anchorPosition.y)
                : anchorPosition.y;

            // Align right edge of dropdown with right edge of profile button
            const leftPosition = anchorRight - dropdownWidth;
            setDropdownPosition({
                top: anchorBottom + 8,
                width: dropdownWidth,
                left: Math.max(16, leftPosition),
            });
        } else if (!visible) {
            setDropdownPosition(null);
        }
    }, [visible, anchorPosition, dropdownWidth]);

    const handleMenuItemPress = (route: string) => {
        onClose();
        router.push(route as any);
    };

    const handleLogoutPress = () => {
        onClose();
        trackProfileLogoutPress();
        setLogoutDialogVisible(true);
    };

    const handleLogoutConfirm = async () => {
        setLogoutDialogVisible(false);
        trackProfileLogoutConfirm();
        try {
            await logout();
            trackProfileLogoutSuccess();
            if (Platform.OS === 'web') {
                void navigateToWelcomeAfterLogout({ showToast: true });
            } else {
                router.dismissAll();
                router.replace('/logout-success' as any);
            }
        } catch (error) {
            logger.error('UserProfileDropdown:Logout failed', error);
            const message =
                error instanceof Error ? error.message : t('profile.logoutError');
            trackProfileLogoutError(message);
            setLogoutErrorVisible(true);
        }
    };

    const logoutConfirmation = (
        <>
            <ConfirmationDialog
                visible={logoutDialogVisible}
                message={t('profile.logoutConfirmation')}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleLogoutConfirm}
                onCancel={() => setLogoutDialogVisible(false)}
            />
            <OkDialog
                visible={logoutErrorVisible}
                message={t('profile.logoutError')}
                onOk={() => setLogoutErrorVisible(false)}
            />
        </>
    );

    if (!visible) return logoutConfirmation;

    return (
        <>
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={[{ flex: 1 }, Platform.OS === 'web' ? { direction: isRTL ? 'rtl' : 'ltr' } : null] as any}>
            <Pressable style={styles.overlay} onPress={onClose}>
                <Pressable
                    style={[
                        styles.dropdown,
                        { backgroundColor: colors.backgroundLevel1 },
                        dropdownPosition || {},
                    ]}
                    onPress={(e) => e.stopPropagation()}
                >
                    {/* Language Switch */}
                    <View style={styles.languageSection}>
                        <Typography
                            variant="bodyLarge"
                            color="secondary"
                            style={[styles.languageLabel, { textAlign: isRTL ? 'right' : 'left' }]}
                        >
                            {t('profile.appLanguage')}
                        </Typography>
                        <LanguageSwitch value={currentLanguage} onChange={(lang) => {
                            onClose();
                            setTimeout(() => {
                                void changeLanguage(lang);
                            }, 10);
                        }} />
                    </View>

                    {/* Menu Items */}
                    <View style={styles.menuItems}>
                        <ProfileMenuRow
                            isRTL={isRTL}
                            borderColor={colors.outlineActive}
                            iconColor={colors.fontPrimary}
                            label={isAdhaJourney ? t('profile.personalDetails') : t('profile.accountSecurity')}
                            onPress={() => handleMenuItemPress('/account-security')}
                            icon={<IdCardLanyardIcon size={24} color={colors.fontPrimary} />}
                        />

                        {isAdhaJourney ? (
                            <ProfileMenuRow
                                isRTL={isRTL}
                                borderColor={colors.outlineActive}
                                iconColor={colors.fontPrimary}
                                label={t('profile.loanDetails')}
                                onPress={() => handleMenuItemPress('/loan-details')}
                                icon={<BuildingIcon size={24} color={colors.fontPrimary} />}
                            />
                        ) : null}

                        <ProfileMenuRow
                            isRTL={isRTL}
                            borderColor={colors.outlineActive}
                            iconColor={colors.fontPrimary}
                            label={t('profile.faq')}
                            onPress={() => handleMenuItemPress('/faq')}
                            icon={<CircleQuestionMarkIcon size={24} color={colors.fontPrimary} />}
                        />

                        <ProfileMenuRow
                            isRTL={isRTL}
                            borderColor={colors.outlineActive}
                            iconColor={colors.fontPrimary}
                            label={t('profile.customerSupport')}
                            onPress={() => handleMenuItemPress('/service-request')}
                            icon={<HeadsetIcon size={24} color={colors.fontPrimary} />}
                        />
                    </View>

                    {/* Log Out Button */}
                    <View style={styles.logoutSection}>
                        <Pressable
                            style={styles.logoutButton}
                            onPress={handleLogoutPress}
                        >
                            <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                                <LogOutIcon size={18} color={colors.fontPrimary} />
                            </View>
                            <View style={styles.logoutTextContainer}>
                                <Typography variant="body" color="primary" style={styles.logoutText}>
                                    {t('profile.logOut')}
                                </Typography>
                                <View style={[styles.logoutUnderline, { backgroundColor: colors.fontPrimary }]} />
                            </View>
                        </Pressable>
                    </View>
                </Pressable>
            </Pressable>
            </View>
        </Modal>
        {logoutConfirmation}
        </>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
    },
    dropdown: {
        position: 'absolute',
        borderRadius: 12,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
        paddingTop: 24,
        paddingBottom: 24,
        paddingHorizontal: 24,
        gap: 12,
    },
    languageSection: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        paddingVertical: 8,
        paddingLeft: 8,
    },
    languageLabel: {
        flex: 1,
    },
    menuItems: {
        gap: 12,
    },
    menuItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 24,
        paddingHorizontal: 16,
        borderRadius: 12,
        borderWidth: 1,
    },
    menuItemText: {
        flex: 1,
    },
    logoutSection: {
        paddingTop: 12,
        width: '100%',
    },
    logoutButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 12,
        justifyContent: 'center',
        width: '100%',
    },
    logoutTextContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    logoutText: {
        textAlign: 'center',
    },
    logoutUnderline: {
        height: 1,
        width: '100%',
        marginTop: 2,
    },
});

export default UserProfileDropdown;

